import { Box, Chip, Typography } from '@mui/material';
import { filter, includes, map } from 'lodash';
import PropTypes from 'prop-types';
import { useForm } from 'react-final-form';

import colors from 'common/constants/colors';

import {
  CONTACT_FIELDS,
  buildContactFieldName,
  formatOnFileValueForEditing,
  isPartnerContactEdited,
  isRemovalConfirmationNeeded,
} from '../../utils/partnerContactsForm';
import PartnerContactField from './PartnerContactField';
import RemovePartnerContactAlert from './RemovePartnerContactAlert';
import partnerContactPropType from './partnerContactPropType';

const PartnerContactSection = ({
  index,
  partnerContactOnFile,
  formContact,
  activeFieldName,
  confirmedRemovalFieldNames,
  unconfirmedRemovalFieldNames,
  isSaving,
  onConfirmRemoval,
  registerInput,
  focusField,
}) => {
  const form = useForm();
  const { partnerName, hasPlaceholderEmail } = partnerContactOnFile;
  const isEdited = isPartnerContactEdited({ partnerContactOnFile, formContact });

  const contactFields = map(CONTACT_FIELDS, (contactField) => {
    const fieldName = buildContactFieldName(index, contactField.fieldKind);

    return {
      contactField,
      fieldName,
      onFileValue: partnerContactOnFile[contactField.fieldKind],
      isUnconfirmedRemoval: includes(unconfirmedRemovalFieldNames, fieldName),
    };
  });

  // Hidden while the field is focused, so emptying it to type a new value does not interrupt.
  const fieldsAwaitingRemovalConfirmation = filter(
    contactFields,
    ({ fieldName, isUnconfirmedRemoval }) => isUnconfirmedRemoval && activeFieldName !== fieldName
  );

  const clearField = ({ fieldName, onFileValue }) => {
    form.change(fieldName, undefined);

    // Otherwise the removal confirmation opens and takes the focus.
    if (!isRemovalConfirmationNeeded({ onFileValue, fieldName, confirmedRemovalFieldNames })) {
      focusField(fieldName);
    }
  };

  const restoreOnFileValue = ({ contactField, fieldName, onFileValue }) => {
    form.change(fieldName, formatOnFileValueForEditing(contactField.fieldKind, onFileValue));
    focusField(fieldName);
  };

  const confirmRemoval = (fieldName) => {
    onConfirmRemoval(fieldName);
    focusField(fieldName);
  };

  return (
    <Box component='section' aria-label={partnerName} sx={{ py: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
        <Typography component='h3' variant='body2' sx={{ fontWeight: 500 }}>
          {partnerName}
        </Typography>
        {isEdited && (
          <Chip
            label='Edited'
            size='small'
            sx={{
              height: 18,
              fontSize: 11,
              fontWeight: 500,
              letterSpacing: '0.3px',
              color: colors.editedFieldBlueLabel,
              bgcolor: colors.editedChipBlue,
              '& .MuiChip-label': { px: '7px' },
            }}
          />
        )}
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 2,
          alignItems: 'start',
        }}
      >
        {map(contactFields, ({ contactField, fieldName, onFileValue, isUnconfirmedRemoval }) => (
          // The email takes the whole row so a long address is never cut off; the phone sits
          // under it at half width.
          <Box
            key={fieldName}
            sx={{ gridColumn: contactField.fieldKind === 'email' ? '1 / -1' : 'auto', minWidth: 0 }}
          >
            <PartnerContactField
              id={`PartnerContactsDialog-${index}-${contactField.fieldKind}`}
              name={fieldName}
              contactField={contactField}
              onFileValue={onFileValue}
              hasPlaceholderOnFile={contactField.fieldKind === 'email' && !!hasPlaceholderEmail}
              isUnconfirmedRemoval={isUnconfirmedRemoval}
              isDisabled={isSaving}
              clearLabel={`Clear ${partnerName} ${contactField.noun}`}
              onClear={() => clearField({ fieldName, onFileValue })}
              inputRef={registerInput(fieldName)}
            />
          </Box>
        ))}
      </Box>

      {map(fieldsAwaitingRemovalConfirmation, (contactFieldState) => (
        <RemovePartnerContactAlert
          key={contactFieldState.fieldName}
          partnerName={partnerName}
          noun={contactFieldState.contactField.noun}
          onBack={() => restoreOnFileValue(contactFieldState)}
          onRemove={() => confirmRemoval(contactFieldState.fieldName)}
        />
      ))}
    </Box>
  );
};

PartnerContactSection.propTypes = {
  index: PropTypes.number.isRequired,
  partnerContactOnFile: partnerContactPropType.isRequired,
  formContact: PropTypes.shape({
    email: PropTypes.string,
    phoneNumber: PropTypes.string,
  }),
  activeFieldName: PropTypes.string,
  confirmedRemovalFieldNames: PropTypes.arrayOf(PropTypes.string).isRequired,
  unconfirmedRemovalFieldNames: PropTypes.arrayOf(PropTypes.string).isRequired,
  isSaving: PropTypes.bool.isRequired,
  onConfirmRemoval: PropTypes.func.isRequired,
  registerInput: PropTypes.func.isRequired,
  focusField: PropTypes.func.isRequired,
};

PartnerContactSection.defaultProps = {
  formContact: {},
  activeFieldName: undefined,
};

export default PartnerContactSection;
